import { Plugin } from "prosemirror-state";
import type { EditorView, NodeView } from "prosemirror-view";
import type { Node } from "prosemirror-model";
import { createRoot, type Root } from "react-dom/client";
import { ReactFlow, ReactFlowProvider, type Node as RFNode, type Edge } from "@xyflow/react";
import { parseMermaidToReactFlow, type FlowchartNodeData } from "@/lib/mermaid-reactflow-converter";
import { flowchartNodeTypes } from "./FlowchartNodes";
import "@xyflow/react/dist/style.css";

// Read-only React Flow preview rendered inside ProseMirror
function MermaidPreview({
    nodes,
    edges,
}: {
    nodes: RFNode<FlowchartNodeData>[];
    edges: Edge[];
}) {
    return (
        <ReactFlowProvider>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={flowchartNodeTypes}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable={false}
                panOnDrag={false}
                zoomOnScroll={false}
                zoomOnPinch={false}
                zoomOnDoubleClick={false}
                preventScrolling={false}
                fitView
                fitViewOptions={{ padding: 0.2 }}
                proOptions={{ hideAttribution: true }}
                className="bg-transparent"
            />
        </ReactFlowProvider>
    );
}

class MermaidNodeViewImpl implements NodeView {
    dom: HTMLElement;
    contentDOM: undefined = undefined;

    private node: Node;
    private readonly onEdit: (pos: number, source: string) => void;
    private readonly onDelete: (pos: number) => void;
    private readonly getPos: () => number | undefined;
    private reactRoot: Root | null = null;

    constructor(
        node: Node,
        _view: EditorView,
        getPos: () => number | undefined,
        onEdit: (pos: number, source: string) => void,
        onDelete: (pos: number) => void,
    ) {
        this.node = node;
        this.getPos = getPos;
        this.onEdit = onEdit;
        this.onDelete = onDelete;

        this.dom = document.createElement("div");
        this.dom.className = "mermaid-preview";
        this.dom.setAttribute("contenteditable", "false");

        // Delete button (top-right)
        const deleteBtn = document.createElement("button");
        deleteBtn.className = "mermaid-preview-delete";
        deleteBtn.innerHTML = "&#x2715;"; // ✕
        deleteBtn.title = "Delete diagram";
        deleteBtn.addEventListener("click", this.handleDelete);
        this.dom.appendChild(deleteBtn);

        // Overlay container
        const overlay = document.createElement("div");
        overlay.className = "mermaid-preview-overlay";
        overlay.textContent = "Edit Diagram";
        this.dom.appendChild(overlay);

        // React Flow container
        const rfContainer = document.createElement("div");
        rfContainer.className = "mermaid-preview-rf";
        this.dom.appendChild(rfContainer);

        this.dom.addEventListener("click", this.handleClick);

        // Mount React Flow preview
        this.reactRoot = createRoot(rfContainer);
        this.renderPreview();
    }

    private handleClick = (e: MouseEvent) => {
        // Don't open editor if the delete button was clicked
        if ((e.target as HTMLElement).closest(".mermaid-preview-delete")) return;
        const pos = this.getPos();
        if (pos !== undefined) {
            this.onEdit(pos, this.node.textContent);
        }
    };

    private handleDelete = (e: MouseEvent) => {
        e.stopPropagation();
        const pos = this.getPos();
        if (pos !== undefined) {
            this.onDelete(pos);
        }
    };

    private renderPreview() {
        const source = this.node.textContent;
        if (!source.trim()) {
            this.reactRoot?.render(
                <p style={{ color: "var(--muted-foreground, #888)", fontStyle: "italic", textAlign: "center" }}>
                    Empty diagram — click to edit
                </p>,
            );
            return;
        }
        try {
            const { nodes, edges } = parseMermaidToReactFlow(source);
            if (nodes.length === 0) {
                this.reactRoot?.render(
                    <p style={{ color: "var(--muted-foreground, #888)", fontStyle: "italic", textAlign: "center" }}>
                        Empty diagram — click to edit
                    </p>,
                );
                return;
            }
            this.reactRoot?.render(<MermaidPreview nodes={nodes} edges={edges} />);
        } catch {
            this.reactRoot?.render(
                <p style={{ color: "var(--destructive, red)", fontStyle: "italic", textAlign: "center" }}>
                    Invalid diagram — click to edit
                </p>,
            );
        }
    }

    stopEvent() {
        return true;
    }

    ignoreMutation() {
        return true;
    }

    update(node: Node): boolean {
        if (node.type !== this.node.type) return false;
        if (node.attrs.params !== "mermaid") return false;
        this.node = node;
        this.renderPreview();
        return true;
    }

    destroy() {
        this.dom.removeEventListener("click", this.handleClick);
        const root = this.reactRoot;
        this.reactRoot = null;
        if (root) {
            setTimeout(() => root.unmount(), 0);
        }
    }
}

/**
 * Creates a ProseMirror plugin that renders `code_block` nodes with
 * `params === "mermaid"` as read-only React Flow previews. Non-mermaid
 * code blocks are left to the default rendering.
 */
export function createMermaidNodeViewPlugin(
    onEdit: (pos: number, source: string) => void,
    onDelete: (pos: number) => void,
) {
    return new Plugin({
        props: {
            nodeViews: {
                code_block(node: Node, view: EditorView, getPos: () => number | undefined): NodeView | undefined {
                    if (node.attrs.params === "mermaid") {
                        return new MermaidNodeViewImpl(node, view, getPos, onEdit, onDelete);
                    }
                    return undefined as any;
                },
            },
        },
    });
}
